import React, { useEffect, useMemo, useRef } from 'react';
import styles from './ImageFileUpload.module.scss';
import Text from '../../../Text';
import LinkButton from '../../../Button/LinkButton/LinkButton';

type Props = {
	value?: File | string | null;
	onChange?: (file: File | null) => void;
	name?: string;
	accept?: string;
	disabled?: boolean;
};

const ImageFileUpload: React.FC<Props> = ({
	value = null,
	onChange,
	name,
	accept,
	disabled,
}) => {
	const hiddenFileInput = useRef<HTMLInputElement | null>(null);

	const previewUrl = useMemo(() => {
		if (!value) return null;
		if (typeof value === 'string') return value;
		if (value instanceof Blob) return URL.createObjectURL(value);
		return null;
	}, [value]);

	useEffect(() => {
		if (!previewUrl?.startsWith('blob:')) return;
		return () => URL.revokeObjectURL(previewUrl);
	}, [previewUrl]);

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const fileUploaded = e.currentTarget.files?.[0] ?? null;
		onChange?.(fileUploaded);
	};

	const handleFilePick = () => {
		if (!disabled) hiddenFileInput.current?.click();
	};

	let component = (
		<div
			className={styles.uploadButton}
			onClick={handleFilePick}
			aria-disabled={disabled}
		>
			<div className={styles.placeholder}>
				<Text tx='base.shared.pickFile' />
				<i className='fa-solid fa-angle-right' aria-hidden='true'></i>
			</div>
		</div>
	);

	if (value && previewUrl) {
		component = (
			<div className={styles.preview}>
				<img src={previewUrl} className={styles.image} alt='imagePreview' />
				<LinkButton
					text='base.change'
					theme='purple'
					onClick={handleFilePick}
				/>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			{component}
			<input
				ref={hiddenFileInput}
				name={name}
				type='file'
				accept={accept}
				disabled={disabled}
				onChange={handleOnChange}
				className={styles.input}
			/>
		</div>
	);
};

export default ImageFileUpload;
